import 'regenerator-runtime/runtime'

import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js'
import { GammaCorrectionShader } from 'three/examples/jsm/shaders/GammaCorrectionShader.js'
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js'
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import CameraControls from 'camera-controls'
import ViewerScene from './viewerScene'
import store from '~/js/store'
import { TweenMax } from "gsap/all"; 
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import Config from './viewerConfig'
import {replaceConfig, getParameterByName, getIsSp, getTimeLog } from './utils';
import MaterialList from './materiallist'
import MaterialXMLParcer from './materialXMLParcer'
import { loadDDS, loadTexture } from './loaderUtils'


CameraControls.install( { THREE: THREE } );
const EPS = 1e-5;
const ENABLE_CAMERA_CONTROL = true;

class MaterialResourceManager{

  
  constructor(){
    this.list = {}; 
  } 

  add(id, map, normalMap, material){
    if(this.list[`${id}`]){
      return false;
    }
    
    this.list[`${id}`] = {};
    this.list[`${id}`].map = map;
    this.list[`${id}`].normalMap = normalMap;
    this.list[`${id}`].material = material;
  }

  get(id){
    return this.list[`${id}`]
  }

  dispose(id){
    if(this.list[`${id}`].map)
      this.list[`${id}`].map.dispose();
    if(this.list[`${id}`].normalMap)
      this.list[`${id}`].normalMap.dispose();
    if(this.list[`${id}`].material)
      this.list[`${id}`].material.dispose();

    delete this.list[`${id}`];
  }

  disposeAll(){
    for(const id in this.list){
      this.dispose(id);
    }
  }
}

class Renderer{
  constructor(){
  }

  async init(){
    this.bFristFrame = true;
    this.bRender = true;
    this.clicked = false;
    this.doubleClicked = false
    this.clickTimeout = null;
    this.lastFrameTime = 0;
    this.fps = 0;
    this.touchMoveCount = 0;
    this.materialResourceManager = new MaterialResourceManager();
    store.commit('debug/updateTime', getTimeLog('app init'))

    // add events
    window.addEventListener( 'resize', this.onWindowResize.bind(this), false );
    window.addEventListener( 'mousemove', this.updateMousePositionFromEvent.bind(this));
    window.addEventListener( 'touchstart', this.updateMousePositionFromEvent.bind(this));
    store.watch(store.getters['material/getIsShowMaterial'], this.onSelectMaterial.bind(this));

    // setup renderer
    this.renderer = new THREE.WebGLRenderer( { antialias: true } );
    this.renderer.setPixelRatio( window.devicePixelRatio );
    this.renderer.setSize( window.innerWidth, window.innerHeight );
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true; // create shadow map at once (needsUpdate will turn false automatically on next frame)
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.info.autoReset = false;
    this.renderer.setClearColor( 0x999999, 1);
    this.renderer.autoClear = false;
    document.body.appendChild( this.renderer.domElement );


    // create camera
    this.camera = new THREE.PerspectiveCamera( 45, window.innerWidth/window.innerHeight, 0.25, 10000);
    this.camera.fov = 50;
    const camPos = new THREE.Vector3(4, 13, 0);
    this.camera.position.set(camPos.x, camPos.y, camPos.z);

    // create scene
    const sceneName = getParameterByName('sceneName'); 
    this.viewerConfig = Config[`${sceneName}`];
    if(this.viewerConfig == null)
      this.viewerConfig = Config.WebGLTest2;
      
    replaceConfig(this.viewerConfig);
    this.scene = new ViewerScene(this.viewerConfig);
    store.commit('debug/updateConfig', this.viewerConfig);
    await this.scene.init();
    store.commit('debug/updateTime', getTimeLog('first models'))

    // camera controls
    this.cameraControls = new CameraControls( this.camera, this.renderer.domElement );
    this.cameraControls.mouseButtons.right = CameraControls.ACTION.NONE; // disable move camera
    this.cameraControls.mouseButtons.wheel = CameraControls.ACTION.NONE; // use zoom instead of ZOOM
    this.cameraControls.touches.two = CameraControls.ACTION.NONE;  // zoom only (disable move camera)
    this.cameraControls.azimuthRotateSpeed = - 0.3; // negative value to invert rotation direction
    this.cameraControls.polarRotateSpeed   = - 0.3; // negative value to invert rotation direction
    this.cameraControls.draggingDampingFactor = 1;
    this.cameraControls.dampingFactor = 1;
    this.cameraControls.truckSpeed = 1 / EPS * 3;
    this.cameraControls.setTarget(camPos.x, camPos.y, camPos.z+EPS);
    this.cameraControls.saveState();
    this.cameraControls.addEventListener('controlstart', this.onControlEvent.bind(this));
    this.cameraControls.addEventListener('control', this.onControlEvent.bind(this));
    this.cameraControls.addEventListener('controlend', this.onControlEvent.bind(this));
    const p = this.scene.getDefaultCameraPosition() || new THREE.Vector3(4, 3, 0);
    this.cameraControls.moveTo(p.x, p.y, p.z, false);

    if(!ENABLE_CAMERA_CONTROL){
      this.orbitControls = new OrbitControls( this.camera, this.renderer.domElement);
      this.orbitControls.minDistance = 2;
      this.orbitControls.maxDistance = 10000;
      this.orbitControls.target.set( 0, 0, 0 );
    }

    // raycaster
    this.bWillRaycast = false;
    this.rayCaster = new THREE.Raycaster();
    this.outlineTargetObjects= [];
    this.choosedObject = null;
    this.currentMousePosition = null;

    // create effect composer
    this.composer = new EffectComposer(this.renderer);
    this.setupRenderPass();
    // clock
    this.clock = new THREE.Clock();
    
    this.renderer.compile(this.scene, this.camera);
    store.commit('debug/updateTime', getTimeLog('shader compiled'))

    // execute render loop
    this.render();
    store.commit('debug/updateTime', getTimeLog('render first frame'))


    // hide loading
    store.commit('loading/hide');
    
    // シーン切り替え、背景ローディングはローディング後に行う
    await this.changeScene(this.viewerConfig.initialViewName);
    await this.scene.loadBackground();

    this.bFristFrame = true;
    
  }

  onWindowResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize( window.innerWidth, window.innerHeight );
    this.bFristFrame = true;
  }

  updateMousePositionFromEvent(e){
    const isTouchEvent = e.type.indexOf('touch') != -1;
    let event = e; 
    if(isTouchEvent){
      event = e.changedTouches[0];
    }
    this.currentMousePosition = this.getMousePosition(event);
  }

  onControlEvent(e){
    // update mouse position
    this.updateMousePositionFromEvent(e.originalEvent);

    const type = e.originalEvent.type;

    const cancelClick = ()=>{
      this.clicked = false;
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }

    const singleClick = ()=>{
      const intersects = this.rayCast(this.currentMousePosition);
      if(!intersects[0])
        return;
      if(!intersects[0].object.touchable)
        return;
      
      const materialListName = intersects[0].object.p3d_material_name ? intersects[0].object.p3d_material_name : '';
      store.commit('material/show', materialListName);
      this.choosedObject = intersects[0].object;
      this.renderer.domElement.style.cursor = 'auto'
    }

    const doubleClick = ()=>{
      // raycast and move hit position
      const intersects = this.rayCast(this.currentMousePosition);
      if(intersects[0]){
        if(intersects[0].object.movable)
        {
          const point = intersects[0].point;
          const camPos = this.camera.position;
          this.cameraMove(new THREE.Vector3(point.x, camPos.y, point.z));
        }
      }
    }

    if((type == 'mousedown' || type == 'touchstart') && this.clicked == false){
      this.clicked = true;
      this.doubleClicked = false;
      this.clickTimeout = setTimeout(()=>{
        this.clicked = false;
        this.clickTimeout = null;
        singleClick();
      }, 400);
    }else if((type == 'mousedown' || type == 'touchstart') && this.clicked == true){
      this.doubleClicked = true;
      cancelClick();
    }else if((type == 'mouseup' || type == 'touchend') && this.doubleClicked == true){
      doubleClick();
    }

    if(type == 'mousemove'){
      cancelClick();
      this.doubleClicked = false;
    }

    // スマホでタッチムーブが1,2回起きてダブルクリック判定にならないという使いづらい問題を
    // 解決するためにtouchMoveCountをつけて、5回以上続くまでは許容範囲にする
    if(type == 'touchmove'){
      this.touchMoveCount++;
      if(this.touchMoveCount > 5){
        cancelClick();
        this.doubleClicked = false;
      }
    }
    else
      this.touchMoveCount = 0;

  }

  onSelectMaterial(val){
    this.bRender = !val;
  }

  cameraMove(targetPosition){
    const currentPosition = this.cameraControls.getPosition();
    let tmp = new THREE.Vector3();
    TweenMax.fromTo(tmp, 1, {...currentPosition}, {...targetPosition, onStart:()=>{
      this.cameraControls.enabled = false;
    }, onUpdate: ()=>{
      this.cameraControls.moveTo(tmp.x, tmp.y, tmp.z, false);
    }, onComplete: ()=>{
      this.cameraControls.enabled = true;
    }})
  }

  async changeScene(sceneName){
    const p = this.scene.getDefaultCameraPositionBySceneName(sceneName);
    this.cameraControls.moveTo(p.x, p.y, p.z, false);
    const angle = this.scene.getDefaultCameraAngleBySceneName(sceneName);
    this.cameraControls.rotateTo(angle.x, angle.y, false); // azimath, polar, isTransitionAnim
    store.commit('debug/updateTime', getTimeLog(`start scene [${sceneName}]`));
    const bNeedUpdate = await this.scene.change(sceneName);
    store.commit('debug/updateTime', getTimeLog(`end scene [${sceneName}]`));

    if(!bNeedUpdate) return;

    store.commit('material/hide');
    this.choosedObject = null;
    
    // update internal state
    this.bFristFrame = true;
  }


  async changeMaterial(materialListName, materialName){

    // materiallist.jsからデータを引っ張る(将来的に別のjsonなどをもらう？)
    const material = MaterialList[`${materialListName}`][`${materialName}`]
    if(!material){
      console.error("Choosed Material File is not found");
      return;
    }

    // xmlをパスしてパラメータをもらう
    const materialFilePath = material.xml;
    const materialParam = await MaterialXMLParcer.Load(materialFilePath);

    // material resource managerから選択したidのリソースを取る
    const materialResources = this.materialResourceManager.get(materialParam.id);
    let mat;

    // 存在する場合はそれを使う
    if(materialResources){
      mat = materialResources.material;
    
    //存在しない場合はxmlをリクエストしてマテリアルを作成
    }else{
      // マテリアル用のテクスチャを用意
      let map, normalMap;
      const imageRequests = []; // 0: map , 1: normalmap

      // ddsかpngでTHREE.jsのローダーが違う。現在はpngでしかこないようになっている(詳細はParcer側)
      if(materialParam.mapExt == 'dds')
        imageRequests[0] = loadDDS(materialParam.mapPath);
      else if(materialParam.mapExt == 'png')
        imageRequests[0] = loadTexture(materialParam.mapPath);

      if(materialParam.normalMapExt == 'dds')
        imageRequests[1] = loadDDS(materialParam.normalMapPath);
      else if(materialParam.normalMapExt == 'png')
        imageRequests[1] = loadTexture(materialParam.normalMapPath);
      
      // pararell request
      [map, normalMap] = await Promise.all(imageRequests);
      materialParam.map = map;
      materialParam.normalMap = normalMap;

      console.log(map,normalMap)
      // テクスチャのパラメータを変える
      if(map){
        map.wrapS = THREE.RepeatWrapping;
        map.wrapT = THREE.RepeatWrapping;
        map.encoding = THREE.sRGBEncoding;
        map.repeat = materialParam.mapScale;
      }
      if(normalMap){
        normalMap.wrapS = THREE.RepeatWrapping;
        normalMap.wrapT = THREE.RepeatWrapping;
        normalMap.encoding = THREE.LINEAREncoding;
        normalMap.repeat = materialParam.normalapScale;
      }

      // 元あるマテリアルを複製してパラメータを上書き
      if(this.choosedObject.replacedMaterial)
        mat = this.choosedObject.replacedMaterial.clone();
      else
        mat = this.choosedObject.material.clone();
      if(map)
        mat.map = map;
      if(normalMap)
        mat.normalMap = normalMap;
      mat.color = materialParam.color;

      this.materialResourceManager.add(materialParam.id, map, normalMap, mat);
    }

    if(mat && this.choosedObject){

      // シーンが削除されるとき、replacedMaterialが存在する場合、そっちを削除する
      if(!this.choosedObject.replacedMaterial)
        this.choosedObject.replacedMaterial = this.choosedObject.material
      this.choosedObject.material = mat;
      this.choosedObject = null;
      this.bFristFrame = true;
    }
  }

  setupRenderPass(){
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    this.outlinePass = new OutlinePass( new THREE.Vector2( window.innerWidth, window.innerHeight ), this.scene, this.camera );
    this.outlinePass.edgeStrength = 3.0;
    this.composer.addPass( this.outlinePass );

    this.fxaaPass = new ShaderPass(FXAAShader);
    const pixelRatio = this.renderer.getPixelRatio();

    this.fxaaPass.material.uniforms['resolution'].value.x = 1 / ( window.innerWidth * pixelRatio );
    this.fxaaPass.material.uniforms['resolution'].value.y = 1 / ( window.innerHeight * pixelRatio );
    this.composer.addPass(this.fxaaPass);

    this.smaaPass = new SMAAPass( window.innerWidth * this.renderer.getPixelRatio(), 
                                  window.innerHeight * this.renderer.getPixelRatio() );
    // this.composer.addPass( this.smaaPass );

    this.gammaPass = new ShaderPass(GammaCorrectionShader);
    this.composer.addPass(this.gammaPass);

  }

  rayCast(targetPos){
    this.rayCaster.setFromCamera(targetPos, this.camera);
    // calculate objects intersecting the picking ray
    const target = this.scene.getRayTargetObjects();
   const intersects = this.rayCaster.intersectObjects(target);
    return intersects;
    
  }

  getMousePosition(e){
    const clientX = e.clientX;
    const clientY = e.clientY;
    const mousePosition = new THREE.Vector2();
    const canvas = this.renderer.domElement;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const canvasW = parseInt(canvas.style.width);
    const canvasH = parseInt(canvas.style.height)
    mousePosition.x = ((clientX - rect.left) / canvasW) * 2 - 1;
    mousePosition.y = -((clientY - rect.top) / canvasH) * 2 + 1;
    return mousePosition;
  }


  render(){
    const loop = (e)=>{
      requestAnimationFrame( loop );

      const currentTime = e;
      this.fps = 1.0 / ((currentTime - this.lastFrameTime)/1000);
      this.lastFrameTime = currentTime;
      store.commit('debug/updateFps', this.fps);


      // debug control
      if(!ENABLE_CAMERA_CONTROL){
        this.orbitControls.update();
        this.composer.render();
        return;
      }

      // オブジェクト選択中はレンダリングしない
      if(!this.bRender)
        return;

      // update controls
      const delta = this.clock.getDelta();
      const updated = this.cameraControls.update( delta ) || this.scene.isAnimate;

      let bRenderFlag = false;
      // spのときはカーソルを表示しないので非表示にして置くだけ
      if(getIsSp() == true){
        this.scene.cursor.visible = false;
        this.renderer.domElement.style.cursor = 'auto'
      }
      // pcのときはアウトライン/カーソル更新のレイキャストをする
      else{
        // 更新がない場合マウスからのレイキャストをする
        if(!updated && this.currentMousePosition){
          const intersects = this.rayCast(this.currentMousePosition);
          if(intersects[0]){
            // タッチオブジェクトに触れている場合
            if(intersects[0].object.touchable){
              this.renderer.domElement.style.cursor = 'pointer'

              // 前フレームのoutlineTargetがある and 今のフレームのオブジェクトと同じ場合以外は更新
              let bNeedUpdateOutlineTarget = true;
              if(this.outlineTargetObjects.length > 0){
                if(this.outlineTargetObjects[0] == intersects[0].object){
                  bNeedUpdateOutlineTarget = false;
                }
              }

              // 現在のオブジェクトに更新してレンダーフラグを立てる
              if(bNeedUpdateOutlineTarget){
                this.outlineTargetObjects = [];
                this.outlineTargetObjects.push(intersects[0].object);
                this.outlinePass.selectedObjects = this.outlineTargetObjects;
                bRenderFlag = true;
              }
            // タッチオブジェクトに触れていない場合
            }else{
              // 前フレームで選択中のものがあれば消す
              this.renderer.domElement.style.cursor = 'auto'
              this.outlineTargetObjects = [];
              this.outlinePass.selectedObjects = [];  
            }
            
            // 移動可能オブジェクトに触れている場合
            if(intersects[0].object.movable){
              this.scene.cursor.visible = true;
              const mousePos = intersects[0].point;
              this.scene.cursor.position.set(mousePos.x, mousePos.y+0.1, mousePos.z);
              bRenderFlag = true;
            // 移動可能オブジェクトに触れていない場合
            }else{
              // 前フレームに表示されてたら消す
              if(this.scene.cursor.visible == true){
                this.scene.cursor.visible = false;
                bRenderFlag = true;
              }
            }
          }
        }
      }
      

      // カメラが動いているとき/最初のフレーム/選択オブジェクトの更新があったとき -> レンダリング
      if(this.bFristFrame || updated || bRenderFlag){
        this.renderer.info.reset();
        this.composer.render();
        this.bFristFrame = false;
        store.commit('debug/updateRenderInfo', this.renderer.info);

        // console.log(this.camera.position, this.cameraControls.azimuthAngle, this.cameraControls.polarAngle)
      }
    }
    loop();
  }

}

export default new Renderer();
